import { Field, useFormikContext } from 'formik';
import camelCase from 'lodash.camelcase';
import { FormattedMessage, useIntl } from 'react-intl';
import { DeliveryOption, useCheckoutState } from '../../../utils/checkoutState';
import { getAssetUrl } from '../../../utils/config';
import Paragraph from '../../atoms/Paragraph';
import Price from '../../atoms/Price';
import SubHeadline from '../../atoms/SubHeadline';
import OptionsField from '../../formik/OptionsField';
import { showDefaultError } from '../Notifications';
import './style.scss';

type AdditionalCarrierConfig = {
  i18nKey?: string;
};

const additionalConfig: Record<string, AdditionalCarrierConfig> = {
  dhlse: { i18nKey: 'dhl' },
  glshu: { i18nKey: 'gls' },
  glsdk: { i18nKey: 'gls' },
};

export const getCarrierI18nKey = (key: string) => additionalConfig[key]?.i18nKey ?? camelCase(key);

const groupCarriers = (
  carriers: (Pick<DeliveryOption, 'carrierKey' | 'carrierGroupKey'> & {
    shippingOptions: DeliveryOption[];
  })[],
  deliveryOption: DeliveryOption
) => {
  const carrier = carriers.find(({ carrierGroupKey }) => carrierGroupKey === deliveryOption.carrierGroupKey);
  if (carrier) {
    carrier.shippingOptions.push(deliveryOption);
  } else {
    carriers.push({
      carrierKey: deliveryOption.carrierKey,
      carrierGroupKey: deliveryOption.carrierGroupKey,
      shippingOptions: [deliveryOption],
    });
  }
  return carriers;
};

const CarrierOptions = ({ setLoading }: { setLoading: (isLoading: boolean) => void }) => {
  const { checkoutState, setCarrier } = useCheckoutState();
  const { formatMessage } = useIntl();
  const {
    values: { carrier },
    setFieldValue,
  } = useFormikContext<{ carrier: Pick<DeliveryOption, 'carrierKey' | 'carrierGroupKey' | 'shippingOptionKey'> }>();
  const carriers =
    checkoutState.orderOptions.shipping?.homeDelivery
      ?.filter(({ isDisabled }) => !isDisabled)
      .reduce(groupCarriers, []) ?? [];
  const carrierOptions = carriers.map(({ carrierGroupKey: key }) => {
    const { i18nKey } = additionalConfig[key] ?? { i18nKey: camelCase(key) };
    return {
      key,
      label: formatMessage({ id: `shipping.carrier.options.${i18nKey}` }),
      defaultIcon: getAssetUrl(`carriers/${i18nKey}.svg`),
    };
  });
  const carrierShippingOptions = carriers.find(
    ({ carrierGroupKey }) => carrierGroupKey === carrier.carrierGroupKey
  )?.shippingOptions;
  const shippingOptions =
    carrierShippingOptions?.map(({ shippingOptionKey, serviceCosts, deliveryEstimations }) => ({
      key: shippingOptionKey,
      label: `${formatMessage({ id: `shipping.carrier.options.${camelCase(shippingOptionKey)}` })}${
        deliveryEstimations?.deliveryDays
          ? ` (${formatMessage(
              { id: 'shipping.carrier.deliveryEstimate' },
              { min: deliveryEstimations.deliveryDays.minimum, max: deliveryEstimations.deliveryDays.maximum }
            )})`
          : ''
      }`,
      labelRight:
        serviceCosts?.total !== undefined ? (
          serviceCosts.total !== 0 ? (
            <Price price={serviceCosts.total} isLarge />
          ) : (
            <FormattedMessage id="generic.free" />
          )
        ) : null,
    })) ?? [];

  if (!carrierOptions.length) {
    return (
      <>
        <SubHeadline>
          <FormattedMessage id="shipping.carrier.headline" />
        </SubHeadline>
        <Paragraph>
          <FormattedMessage id="shipping.notifications.noDeliveryMethods" />
        </Paragraph>
      </>
    );
  }

  if (carrierOptions.length === 1 && shippingOptions.length === 1) {
    return null;
  }

  const onChange = async (
    selectedCarrier: Pick<DeliveryOption, 'carrierKey' | 'carrierGroupKey' | 'shippingOptionKey'>
  ) => {
    setLoading(true);
    try {
      await setCarrier(selectedCarrier);
    } catch (e) {
      console.error(e);
      showDefaultError();
    }
    setLoading(false);
  };

  return (
    <>
      <SubHeadline>
        <FormattedMessage id="shipping.carrier.headline" />
      </SubHeadline>
      {carrierOptions.length > 1 && (
        <Field
          name="carrier.carrierGroupKey"
          className="carrier__options"
          component={OptionsField}
          options={carrierOptions}
          dataTestIdPrefix="carrierOptions"
          onChange={(carrierGroupKey: string) => {
            const newCarrier = carriers.find(eachCarrier => eachCarrier.carrierGroupKey === carrierGroupKey)!;
            setFieldValue('carrier.carrierKey', newCarrier.carrierKey);
            const firstShippingOptionKey = newCarrier.shippingOptions[0].shippingOptionKey;
            setFieldValue('carrier.shippingOptionKey', firstShippingOptionKey);
            onChange({
              carrierGroupKey,
              carrierKey: newCarrier.carrierKey,
              shippingOptionKey: firstShippingOptionKey,
            });
          }}
        />
      )}
      {shippingOptions.length > 1 && (
        <div className="carrier-options__carrier-delivery-type-options">
          <Field
            name="carrier.shippingOptionKey"
            component={OptionsField}
            options={shippingOptions}
            dataTestIdPrefix="carrierShippingOptions"
            isVertical
            onChange={(shippingOptionKey: string) => {
              onChange({
                carrierGroupKey: carrier.carrierGroupKey,
                carrierKey: carrier.carrierKey,
                shippingOptionKey,
              });
            }}
          />
        </div>
      )}
    </>
  );
};

export default CarrierOptions;
